import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Bell,
    ChevronDown,
    ArrowRightLeft,
    User,
    Settings2,
    CreditCard,
    LogOut,
    HelpCircle,
    Building2,
    Plus,
    Check,
    ChevronsUpDown,
} from 'lucide-react';
import CircularProgress from '@mui/material/CircularProgress';
import DOMPurify from 'dompurify';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxSubscription } from '@Hooks/reduxSubscriptionHook/useReduxSubscription';
import OrgService from '@Helpers/URLHelpers/OrgService';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import CreateWorkspaceModal from './CreateWorkspaceModal';
import './TopPanelAdmin.scss';

const SUBSCRIBED_STATUSES = new Set(['ACTIVE', 'TRIALING', 'HALTED', 'CREATED']);

// ── Route → page meta ─────────────────────────────────────────────────────────
const ROUTE_META = [
    { match: '/userManagement/teams-users', title: 'Teams & Users', subtitle: 'Project collaborators and invitations' },
    { match: '/userManagement/settings', title: 'Settings',    subtitle: 'Account, security & preferences' },
    { match: '/userManagement',          title: 'My Profile',  subtitle: 'Your personal account' },
    { match: '/dashboard',               title: 'Dashboard',   subtitle: 'Your work at a glance' },
    { match: '/forms',                   title: 'Forms',       subtitle: 'Manage your intake forms' },
    { match: '/registries',              title: 'Registries',  subtitle: 'Structured data registries' },
    { match: '/docs',                    title: 'Docs',        subtitle: 'Team documentation' },
    { match: '/support',                 title: 'Support',     subtitle: 'Help & resources' },
];

const getRouteMeta = (pathname) => {
    // longest prefix wins
    const sorted = [...ROUTE_META].sort((a, b) => b.match.length - a.match.length);
    return sorted.find(r => pathname.startsWith(r.match)) || { title: 'Resolve', subtitle: '' };
};

export default function TopPanelAdmin() {

    const navigate  = useNavigate();
    const location  = useLocation();
    const dropRef   = useRef(null);

    const { activeUser, userType, setUserType, activeUserId, activeUserAccessList, setActiveUserAccessList, setActiveUserPermission } = useReduxActiveUser();
    const { currentOrg, currentUserOrgPermissions, setCurrentOrg } = useReduxOrg();
    const { setCurrentProject }                          = useReduxProject();
    const { subscription }                               = useReduxSubscription();

    // Billing is visible to org admins in addition to the owner — everyone else is a regular
    // member and shouldn't see the "Billing & Plans" shortcut.
    const isOrgOwner = !currentOrg || currentOrg.ownerUserId === activeUserId;
    const canManageBilling = isOrgOwner || !!currentUserOrgPermissions?.allowAdmin;

    const isSubscribed = SUBSCRIBED_STATUSES.has(subscription?.status);

    const [dropOpen,    setDropOpen]    = useState(false);
    const [orgPanelOpen, setOrgPanelOpen] = useState(false);
    const [switchingOrg, setSwitchingOrg] = useState(null);
    const [newOrgModalOpen, setNewOrgModalOpen] = useState(false);
    const [newOrgFormData, setNewOrgFormData] = useState({ orgName: '', defaultResolveTag: '' });
    const [newOrgErrors, setNewOrgErrors] = useState({});
    // Tracks whether the agent has typed into the tag field directly — once they have, the
    // organization name no longer overwrites it. Cleared alongside the rest of the form.
    const [newOrgTagTouched, setNewOrgTagTouched] = useState(false);
    const [creatingOrg, setCreatingOrg] = useState(false);

    const resolveOrgs = (activeUserAccessList?.orgs || []).filter(o => o.visibility === 'resolve');

    const className = 'topPanelAdmin';
    const orgService = new OrgService();
    const userAuthService = new UserAuthService();
    const REFRESH_INTERVAL = 30000;

    const refreshAccessList = async () => {
        try {
            const response = await userAuthService.getAccessListForUser(activeUserId);
            if (response && response.body) {
                const updatedAccessList = JSON.parse(response.body);
                setActiveUserAccessList(updatedAccessList);
            }

            const permissionsResponse = await userAuthService.getUserPermissions(activeUserId);
            if (permissionsResponse && permissionsResponse.body) {
                const updatedPermissions = JSON.parse(permissionsResponse.body);
                setActiveUserPermission(updatedPermissions);
            }
        } catch (err) {
            console.error('Failed to refresh access list:', err);
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handler = (e) => {
            if (dropRef.current && !dropRef.current.contains(e.target)) {
                setDropOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Set up interval to refresh access list periodically
    useEffect(() => {
        const intervalId = setInterval(refreshAccessList, REFRESH_INTERVAL);
        return () => clearInterval(intervalId);
    }, [activeUserId]);

    const { title, subtitle } = getRouteMeta(location.pathname);

    const userName = activeUser?.fullName || activeUser?.userName || 'User';
    const userEmail = activeUser?.email || '';

    const initials = useMemo(() => {
        const parts = (userName || 'U').trim().split(/\s+/);
        const first = parts[0]?.[0] || '';
        const last  = parts.length > 1 ? parts[parts.length - 1]?.[0] : '';
        return (first + (last || '')).toUpperCase();
    }, [userName]);

    const goAndClose = (path) => {
        setDropOpen(false);
        navigate(path);
    };

    const handleUserType = () => {
        const projects = activeUserAccessList?.projects;
        const resolveProject = projects?.find((project) => project.visibility === 'resolve');
        if (resolveProject) {
            setCurrentProject(resolveProject);
        }

        if (userType === 'reporter') {
            setUserType('admin');
            navigate('/dashboard');
        } else {
            setUserType('reporter');
            navigate('/home');
        }
    };

    const handleOrgSwitch = async (org) => {
        if (switchingOrg !== null || org.id === currentOrg?.id) return;
        setSwitchingOrg(org.id);
        try {
            await setCurrentOrg(org);
            const resolveProject = (activeUserAccessList?.projects || []).find(
                p => p.visibility === 'resolve' && p.parentId === org.id
            );
            if (resolveProject) await setCurrentProject(resolveProject);
            navigate('/dashboard', { replace: true });
        } catch (err) {
            console.error(err);
        } finally {
            setSwitchingOrg(null);
            setDropOpen(false);
        }
    };

    // Derives a 6-letter tag from the org name: letters only, uppercased, padded with 'X' when
    // the name is too short. Letters-only output trivially satisfies the "ends with a letter"
    // rule this form already enforces. Returns '' when the name has no letters to draw from
    // (e.g. still empty, or digits/symbols only) rather than emitting an all-'X' placeholder.
    const deriveResolveTag = (orgNameValue) => {
        const letters = (orgNameValue || '').replace(/[^a-zA-Z]/g, '').toUpperCase();
        if (!letters) return '';
        return (letters + 'XXXXXX').slice(0, 6);
    };

    const validateNewOrgForm = () => {
        const validator = /[a-zA-Z]$/;
        const errors = {};
        const tagValue = newOrgFormData.defaultResolveTag?.trim() || '';

        if (!newOrgFormData.orgName?.trim()) {
            errors.orgName = 'Organization Name is required';
        }
        if (!tagValue) {
            errors.defaultResolveTag = 'Default Form Tag is required';
        } else if (!validator.test(tagValue)) {
            errors.defaultResolveTag = 'Tag must end with a letter';
        } else if (tagValue.length !== 6) {
            errors.defaultResolveTag = 'Tag must be exactly 6 characters';
        }

        setNewOrgErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateNewOrg = async () => {
        if (!validateNewOrgForm()) return;

        setCreatingOrg(true);
        try {
            const sanitizedName = DOMPurify.sanitize(newOrgFormData.orgName);
            const sanitizedTag = DOMPurify.sanitize(newOrgFormData.defaultResolveTag);

            const response = await orgService.createOrgForResolveUser({
                userId: activeUserId,
                user: activeUser,
                org: {
                    orgName: sanitizedName,
                    defaultResolveTag: sanitizedTag.toUpperCase()
                }
            });

                if (response.status < 400) {
                    const result = JSON.parse(response?.body);
                    await setCurrentOrg(result?.org);
                    await setCurrentProject(result?.project);
                    
                    await refreshAccessList();
                    
                    setNewOrgModalOpen(false);
                    setNewOrgFormData({ orgName: '', defaultResolveTag: '' });
                    setNewOrgErrors({});
                    setNewOrgTagTouched(false);
                    
                    SnackbarManager.show('Workspace created successfully!', 'success');
                    navigate('/subscribe', { replace: true });
            } else {
                SnackbarManager.show('Failed to create workspace', 'error');
            }
        } catch (err) {
            console.error(err);
            SnackbarManager.show('Error creating workspace', 'error');
        } finally {
            setCreatingOrg(false);
        }
    };

    return (
        <header className={className}>

            {/* ── Left: page title ── */}
            <div className={className + '__left'}>
                <div className={className + '__page'}>
                    <h1 className={className + '__page__title'}>{title}</h1>
                    {subtitle && (
                        <span className={className + '__page__subtitle'}>{subtitle}</span>
                    )}
                </div>
            </div>

            {/* ── Right: actions ── */}
            <div className={className + '__right'}>

                {/* Org name badge — only when subscribed */}
                {isSubscribed && currentOrg?.name && (
                    <span className={className + '__org'}>{currentOrg.name}</span>
                )}

                <button
                    className={className + '__switchRole'}
                    onClick={handleUserType}
                    title={`Switch to ${userType === 'reporter' ? 'Admin' : 'Reporter'}`}
                >
                    <ArrowRightLeft size={14} />
                    <span>{`Switch to ${userType === 'reporter' ? 'Admin' : 'Reporter'}`}</span>
                </button>

                {/* Notification bell — only when subscribed */}
                {isSubscribed && (
                    <button
                        className={className + '__icon-btn'}
                        aria-label="Notifications"
                        title="Notifications (coming soon)"
                    >
                        <Bell size={18} />
                    </button>
                )}

                {isSubscribed && <div className={className + '__divider'} />}

                {/* Avatar + dropdown */}
                <div className={className + '__user'} ref={dropRef}>
                    <button
                        className={className + '__user__trigger'}
                        onClick={() => setDropOpen(prev => !prev)}
                        aria-expanded={dropOpen}
                        aria-haspopup="true"
                    >
                        <div className={className + '__user__avatar'}>
                            {activeUser?.photo && activeUser.photo !== 'null' && activeUser.photo !== ''
                                ? <img src={activeUser.photo} alt={userName} />
                                : <span>{initials}</span>
                            }
                        </div>
                        <span className={className + '__user__name'}>{userName}</span>
                        <ChevronDown
                            size={14}
                            className={className + '__user__chevron' + (dropOpen ? ' is-open' : '')}
                        />
                    </button>

                    {dropOpen && (
                        <div className={className + '__dropdown'} role="menu">

                            {/* User card */}
                            <div className={className + '__dropdown__header'}>
                                <div className={className + '__dropdown__avatar'}>
                                    {activeUser?.photo && activeUser.photo !== 'null' && activeUser.photo !== ''
                                        ? <img src={activeUser.photo} alt={userName} />
                                        : <span>{initials}</span>
                                    }
                                </div>
                                <div className={className + '__dropdown__user-info'}>
                                    <span className={className + '__dropdown__user-name'}>{userName}</span>
                                    <span className={className + '__dropdown__user-email'}>{userEmail}</span>
                                    <span className={className + '__dropdown__user-role'}>{userType || 'Admin'}</span>
                                </div>
                            </div>

                            <div className={className + '__dropdown__divider'} />

                            <button className={className + '__dropdown__item'} onClick={() => goAndClose('/userManagement/settings?tab=1')}>
                                <User size={15} />
                                <span>My Profile</span>
                            </button>
                            <button className={className + '__dropdown__item'} onClick={() => goAndClose('/userManagement/settings?tab=0')}>
                                <Settings2 size={15} />
                                <span>Settings</span>
                            </button>
                            {canManageBilling && (
                                <button className={className + '__dropdown__item'} onClick={() => goAndClose('/userManagement/settings?tab=5')}>
                                    <CreditCard size={15} />
                                    <span>Billing & Plans</span>
                                </button>
                            )}
                            <button className={className + '__dropdown__item'} onClick={() => goAndClose('/support')}>
                                <HelpCircle size={15} />
                                <span>Help & Support</span>
                            </button>

                            <div className={className + '__dropdown__divider'} />

                            {/* Workspace switcher */}
                            <div className={className + '__dropdown__workspace'}>
                                <button
                                    className={className + '__dropdown__workspace__trigger'}
                                    onClick={() => setOrgPanelOpen(prev => !prev)}
                                >
                                    <Building2 size={15} />
                                    <span>Workspace</span>
                                    <ChevronsUpDown
                                        size={13}
                                        className={className + '__dropdown__workspace__chevron' + (orgPanelOpen ? ' is-open' : '')}
                                    />
                                </button>

                                {orgPanelOpen && (
                                    <div className={className + '__dropdown__workspace__list'}>
                                        {resolveOrgs.map(org => (
                                            <button
                                                key={org.id}
                                                className={className + '__dropdown__workspace__org' + (org.id === currentOrg?.id ? ' is-active' : '')}
                                                onClick={() => handleOrgSwitch(org)}
                                                disabled={switchingOrg !== null}
                                            >
                                                <div className={className + '__dropdown__workspace__org__avatar'}>
                                                    {org.orgName?.[0]?.toUpperCase() || 'O'}
                                                </div>
                                                <span className={className + '__dropdown__workspace__org__name'}>{org.orgName}</span>
                                                {switchingOrg === org.id
                                                    ? <CircularProgress size={12} color="inherit" />
                                                    : org.id === currentOrg?.id && <Check size={13} />}
                                            </button>
                                        ))}

                                        <button
                                            className={className + '__dropdown__workspace__new'}
                                            onClick={() => setNewOrgModalOpen(true)}
                                        >
                                            <Plus size={14} />
                                            <span>New workspace</span>
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className={className + '__dropdown__divider'} />

                            <button
                                className={className + '__dropdown__item ' + className + '__dropdown__item--danger'}
                                onClick={() => navigate('/logout')}
                            >
                                <LogOut size={15} />
                                <span>Log out</span>
                            </button>

                        </div>
                    )}
                </div>
            </div>

            <CreateWorkspaceModal
                open={newOrgModalOpen}
                creating={creatingOrg}
                formData={newOrgFormData}
                errors={newOrgErrors}
                onClose={() => setNewOrgModalOpen(false)}
                onSubmit={handleCreateNewOrg}
                onOrgNameChange={(value) => {
                    setNewOrgFormData(prev => ({
                        ...prev,
                        orgName: value,
                        // Keep deriving the tag from the name until the agent edits the tag
                        // field themselves — at that point their edit takes over.
                        defaultResolveTag: newOrgTagTouched ? prev.defaultResolveTag : deriveResolveTag(value),
                    }));
                    if (newOrgErrors.orgName) {
                        setNewOrgErrors(prev => ({ ...prev, orgName: '' }));
                    }
                    if (!newOrgTagTouched && newOrgErrors.defaultResolveTag) {
                        setNewOrgErrors(prev => ({ ...prev, defaultResolveTag: '' }));
                    }
                }}
                onTagChange={(value) => {
                    setNewOrgTagTouched(true);
                    const val = value.toUpperCase().slice(0, 6);
                    setNewOrgFormData(prev => ({ ...prev, defaultResolveTag: val }));
                    if (newOrgErrors.defaultResolveTag) {
                        setNewOrgErrors(prev => ({ ...prev, defaultResolveTag: '' }));
                    }
                }}
            />
        </header>
    );
}
